import { useState } from "react";
import { useSavedQueries, useTranslate, useDataProvider, useNotify } from "react-admin";
import { useResourcePermissions } from "@/hooks/useResourcePermissions";
import { transformClaimantCreate, handleClaimantCreateSuccess } from "@/resources/claimants/ClaimantList";
import { transformVendorCreate } from "@/resources/vendors/VendorsList";
import querystring from "query-string";
import { endOfYesterday } from "date-fns";
import { SolarMenu } from "@react-admin/ra-navigation";
import { Divider, Typography } from "@mui/material";
import {
  Add,
  AdminPanelSettings as AdminPanelSettingsIcon,
  Dashboard,
  Checklist,
  Description,
  Redeem,
  HelpOutline as HelpIcon,
} from "@mui/icons-material";
import { ResourceName, type ClaimantCreate, type ClaimItemCreate, type ClientCreate, type VendorCreate } from "@/types";
import { CreateInDialog } from "../../common/CreateInDialog";
import clients from "@/resources/clients";
import policies from "@/resources/policies";
import claimants from "@/resources/claimants";
import reimbursements from "@/resources/reimbursements";
import vendors from "@/resources/vendors";

// ─── Shared Filters ─────────────────────────────────────────────────────────

const newRecordFilter = querystring.stringify({
  filter: JSON.stringify({ created_datetime_gte: endOfYesterday().toISOString() }),
});

// ─── Core Domain Items ──────────────────────────────────────────────────────

export const ClientsMenuItem = () => {
  const translate = useTranslate();
  const savedQueries = usePersistedQueriesMenu(ResourceName.CLIENTS);
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const { canCreate } = useResourcePermissions(ResourceName.CLIENTS);

  return (
    <>
      <SolarMenu.Item
        name={ResourceName.CLIENTS}
        icon={<clients.icon />}
        label="Clients"
        subMenu={
          <>
            <SubMenuHeading>View</SubMenuHeading>
            <Divider />
            <SolarMenu.List dense>
              <SolarMenu.Item name="clients.all_clients" to="/clients?filter={}" label="All Clients" />
              <SolarMenu.Item name="clients.newcomers" to={`/clients?${newRecordFilter}`} label="New Clients" />
            </SolarMenu.List>
            <SavedQueriesSection resource="clients" queries={savedQueries} translate={translate} />
            {canCreate && (
              <ActionsSection>
                <SolarMenu.Item
                  name="clients.create"
                  label="Create Client"
                  icon={<Add />}
                  onClick={() => setOpenCreateDialog(true)}
                />
              </ActionsSection>
            )}
          </>
        }
      />
      {canCreate && (
        <CreateInDialog<ClientCreate>
          open={openCreateDialog}
          onClose={() => setOpenCreateDialog(false)}
          resource={ResourceName.CLIENTS}
        />
      )}
    </>
  );
};

export const PoliciesMenuItem = () => {
  const savedQueries = usePersistedQueriesMenu(ResourceName.POLICIES);
  const { canCreate } = useResourcePermissions(ResourceName.POLICIES);

  return (
    <SolarMenu.Item
      name={ResourceName.POLICIES}
      icon={<policies.icon />}
      label="Policies"
      subMenu={
        <>
          <SubMenuHeading>View</SubMenuHeading>
          <Divider />
          <SolarMenu.List dense>
            <SolarMenu.Item name="policies.all_policies" to="/policies?filter={}" label="All Policies" />
            <SolarMenu.Item name="policies.newcomers" to={`/policies?${newRecordFilter}`} label="New Policies" />
          </SolarMenu.List>
          <SavedQueriesSection resource="policies" queries={savedQueries} />
          <SubMenuHeading mt={3}>Analytics</SubMenuHeading>
          <Divider />
          <SolarMenu.List>
            <SolarMenu.Item name="policies.dashboard" to="/policies/dashboard" label="Dashboard" icon={<Dashboard />} />
          </SolarMenu.List>
          {canCreate && (
            <ActionsSection>
              <SolarMenu.Item name="policies.create" to="/policies/create" label="Create Policy" icon={<Add />} />
            </ActionsSection>
          )}
        </>
      }
    />
  );
};

export const ClaimantsMenuItem = () => {
  const translate = useTranslate();
  const savedQueries = usePersistedQueriesMenu(ResourceName.CLAIMANTS);
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const { canCreate } = useResourcePermissions(ResourceName.CLAIMANTS);
  // Gate the duplicate-review entry the way this file gates every other list
  // item (see claimItemsPerms.canList below). Without it a role lacking
  // duplicate-claimant-pairs:list gets the link and then "Page Not Found",
  // because App.tsx has already filtered the resource out of the resource list.
  // Not reachable with the roles 000214 grants -- all six carry `list` -- so this
  // is about a custom role later (review !4630 thread 27554).
  const duplicatePairPerms = useResourcePermissions(ResourceName.DUPLICATE_CLAIMANT_PAIRS);
  const dataProvider = useDataProvider();
  const notify = useNotify();

  return (
    <>
      <SolarMenu.Item
        name={ResourceName.CLAIMANTS}
        icon={<claimants.icon />}
        label="Claimants"
        subMenu={
          <>
            <SubMenuHeading>View</SubMenuHeading>
            <Divider />
            <SolarMenu.List dense>
              <SolarMenu.Item name="claimants.all_claimants" to="/claimants?filter={}" label="All Claimants" />
              <SolarMenu.Item name="claimants.newcomers" to={`/claimants?${newRecordFilter}`} label="New Claimants" />
              {/*
                Suspected duplicates (#6717). Lives under Claimants rather than as
                a top-level item: a reviewer reaches it while working claimants,
                and it is a review queue, not a primary entity.
              */}
              {duplicatePairPerms.canList && (
                <SolarMenu.Item
                  name="claimants.duplicates"
                  to={`/${ResourceName.DUPLICATE_CLAIMANT_PAIRS}`}
                  label="Duplicate Claimants"
                />
              )}
            </SolarMenu.List>
            <SavedQueriesSection resource="claimants" queries={savedQueries} translate={translate} />
            {canCreate && (
              <ActionsSection>
                <SolarMenu.Item
                  name="claimants.create"
                  label="Create Claimant"
                  icon={<Add />}
                  onClick={() => setOpenCreateDialog(true)}
                />
              </ActionsSection>
            )}
          </>
        }
      />
      {canCreate && (
        <CreateInDialog<ClaimantCreate>
          open={openCreateDialog}
          onClose={() => setOpenCreateDialog(false)}
          resource={ResourceName.CLAIMANTS}
          transform={transformClaimantCreate}
          onSuccess={handleClaimantCreateSuccess(dataProvider, notify)}
        />
      )}
    </>
  );
};

export const StoplossClaimsMenuItem = () => {
  const translate = useTranslate();
  const savedQueries = usePersistedQueriesMenu(ResourceName.STOPLOSS_CLAIMS);
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const claimsPerms = useResourcePermissions(ResourceName.STOPLOSS_CLAIMS);
  const claimItemsPerms = useResourcePermissions(ResourceName.CLAIM_ITEMS);

  return (
    <>
      <SolarMenu.Item
        name={ResourceName.STOPLOSS_CLAIMS}
        icon={<Redeem />}
        label="Stop-Loss Claims"
        subMenu={
          <>
            <SubMenuHeading>View</SubMenuHeading>
            <Divider />
            <SolarMenu.List dense>
              <SolarMenu.Item
                name="stoploss-claims.all_claims"
                to="/stoploss-claims?filter={}"
                label="All Stop-Loss Claims"
              />
              <SolarMenu.Item
                name="stoploss-claims.newcomers"
                to={`/stoploss-claims?${newRecordFilter}`}
                label="New Stop-Loss Claims"
              />
              {claimItemsPerms.canList && (
                <SolarMenu.Item name="claim-items.all_claims" to="/claim-items?filter={}" label="All Claim Items" />
              )}
            </SolarMenu.List>
            <SavedQueriesSection resource="stoploss-claims" queries={savedQueries} translate={translate} />
            <SubMenuHeading mt={3}>Analytics</SubMenuHeading>
            <Divider />
            <SolarMenu.List>
              <SolarMenu.Item
                name="stoploss-claims.dashboard"
                to="/stoploss-claims/dashboard"
                label="Dashboard"
                icon={<Dashboard />}
              />
            </SolarMenu.List>
            {(claimsPerms.canCreate || claimItemsPerms.canCreate) && (
              <ActionsSection>
                {claimsPerms.canCreate && (
                  <SolarMenu.Item
                    name="stoploss-claims.create"
                    to="/stoploss-claims/create"
                    label="Upload Claims Files"
                    icon={<Add />}
                  />
                )}
                {claimItemsPerms.canCreate && (
                  <SolarMenu.Item
                    name="claim-items.create"
                    label="Create Claim Item"
                    icon={<Add />}
                    onClick={() => setOpenCreateDialog(true)}
                  />
                )}
              </ActionsSection>
            )}
          </>
        }
      />
      {claimItemsPerms.canCreate && (
        <CreateInDialog<ClaimItemCreate>
          open={openCreateDialog}
          onClose={() => setOpenCreateDialog(false)}
          resource={ResourceName.CLAIM_ITEMS}
        />
      )}
    </>
  );
};

export const ReimbursementsMenuItem = () => {
  const translate = useTranslate();
  const savedQueries = usePersistedQueriesMenu(ResourceName.REIMBURSEMENTS);
  const { canCreate } = useResourcePermissions(ResourceName.REIMBURSEMENTS);

  return (
    <SolarMenu.Item
      name={ResourceName.REIMBURSEMENTS}
      icon={<reimbursements.icon />}
      label="Reimbursements"
      subMenu={
        <>
          <SubMenuHeading>View</SubMenuHeading>
          <Divider />
          <SolarMenu.List dense>
            <SolarMenu.Item
              name="reimbursements.all_reimbursements"
              to="/reimbursements?filter={}"
              label="All Reimbursements"
            />
            <SolarMenu.Item
              name="reimbursements.newcomers"
              to={`/reimbursements?${newRecordFilter}`}
              label="New Reimbursements"
            />
          </SolarMenu.List>
          <SavedQueriesSection resource="reimbursements" queries={savedQueries} translate={translate} />
          {canCreate && (
            <ActionsSection>
              <SolarMenu.Item
                name="reimbursements.create"
                to="/reimbursements/create"
                label="Create Reimbursement"
                icon={<Add />}
              />
            </ActionsSection>
          )}
        </>
      }
    />
  );
};

// ─── Workflow Items ─────────────────────────────────────────────────────────

export const TasksMenuItem = () => (
  <SolarMenu.Item icon={<Checklist />} label="Tasks" name={ResourceName.TASKS} to="/tasks" data-testid="tasks-button" />
);

export const DocumentsMenuItem = () => (
  <SolarMenu.Item
    icon={<Description />}
    label="Documents"
    name={ResourceName.DOCUMENTS}
    to="/documents"
    data-testid="documents-button"
  />
);

// ─── Reference Items ────────────────────────────────────────────────────────

export const VendorsMenuItem = () => {
  const translate = useTranslate();
  const savedQueries = usePersistedQueriesMenu(ResourceName.VENDORS);
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const { canCreate } = useResourcePermissions(ResourceName.VENDORS);

  return (
    <>
      <SolarMenu.Item
        name={ResourceName.VENDORS}
        icon={<vendors.icon />}
        label="Vendors"
        subMenu={
          <>
            <SubMenuHeading>View</SubMenuHeading>
            <Divider />
            <SolarMenu.List dense>
              <SolarMenu.Item name="vendors.all_vendors" to="/vendors?filter={}" label="All Vendors" />
              <SolarMenu.Item name="vendors.carriers" to="/vendors/carriers" label="Carriers" />
              <SolarMenu.Item name="vendors.tpas" to="/vendors/tpas" label="TPAs" />
              <SolarMenu.Item name="vendors.rx" to="/vendors/rx" label="Rx Vendors" />
            </SolarMenu.List>
            <SavedQueriesSection resource="vendors" queries={savedQueries} translate={translate} />
            {canCreate && (
              <ActionsSection>
                <SolarMenu.Item
                  name="vendors.create"
                  label="Create Vendor"
                  icon={<Add />}
                  onClick={() => setOpenCreateDialog(true)}
                />
              </ActionsSection>
            )}
          </>
        }
      />
      {canCreate && (
        <CreateInDialog<VendorCreate>
          open={openCreateDialog}
          onClose={() => setOpenCreateDialog(false)}
          resource={ResourceName.VENDORS}
          transform={transformVendorCreate}
        />
      )}
    </>
  );
};

// ─── Bottom Toolbar Items ───────────────────────────────────────────────────

export const AdminMenuItem = () => {
  const rolesPerms = useResourcePermissions(ResourceName.ROLES);
  return (
    <SolarMenu.Item
      icon={<AdminPanelSettingsIcon />}
      label="Admin"
      name="admin"
      data-testid="admin-button"
      subMenu={
        <>
          <SubMenuHeading>Admin</SubMenuHeading>
          <Divider />
          <SolarMenu.List dense>
            <SolarMenu.Item name="admin.users" to="/users?filter={}" label="Users" />
            {rolesPerms.hasAnyPermission && <SolarMenu.Item name="admin.roles" to="/roles" label="Roles" />}
            <SolarMenu.Item name="partners" to="/partners" label="Partners" />
            <SolarMenu.Item name="admin.task-templates" to="/task-templates" label="Global Task Templates" />
            <SolarMenu.Item name="admin.record-recovery" to="/record-recovery" label="Record Recovery" />
          </SolarMenu.List>
        </>
      }
    />
  );
};

// ─── Shared SubMenu Components ──────────────────────────────────────────────

/** Consistent heading used in all submenu flyouts. */
const SubMenuHeading = ({ children, mt }: { children: string; mt?: number }) => (
  <Typography variant="h3" gutterBottom ml={1} mt={mt}>
    {children}
  </Typography>
);

/** Renders the "My Queries" section if saved queries exist for a resource. */
const SavedQueriesSection = ({
  resource,
  queries,
  translate,
}: {
  resource: string;
  queries: { label: string; to: string }[];
  translate?: (key: string, options?: Record<string, unknown>) => string;
}) => {
  if (queries.length === 0) return null;

  const heading = translate ? translate("My Queries", { smart_count: 1 }) : "My Queries";

  return (
    <>
      <Typography variant="h3" gutterBottom mt={2} ml={1}>
        {heading}
      </Typography>
      <Divider />
      <SolarMenu.List dense>
        {queries.map(({ label, to }) => (
          <SolarMenu.Item key={label} name={`${resource}.${label}`} to={to} label={label} />
        ))}
      </SolarMenu.List>
    </>
  );
};

/** Wraps the "Actions" heading + divider pattern used in most submenus. */
const ActionsSection = ({ children }: { children: React.ReactNode }) => (
  <>
    <SubMenuHeading mt={3}>Actions</SubMenuHeading>
    <Divider />
    <SolarMenu.List>{children}</SolarMenu.List>
  </>
);

// ─── Hooks ──────────────────────────────────────────────────────────────────

/** Builds menu items from react-admin's persisted saved queries for a resource. */
const usePersistedQueriesMenu = (resource: string): { label: string; to: string }[] => {
  const [savedQueries] = useSavedQueries(resource);
  return savedQueries.map(({ label, value }) => ({
    label,
    to: `/${resource}?${querystring.stringify({
      filter: JSON.stringify(value.filter),
      sort: value?.sort?.field,
      order: value?.sort?.order,
      page: 1,
      perPage: value.perPage,
      displayedFilters: value.displayedFilters,
    })}`,
  }));
};

// ─── In-App Help / Knowledge Base (Feature 4317) ─────────────────────────────

export const HelpMenuItem = () => {
  const { canCreate } = useResourcePermissions(ResourceName.HELP_ARTICLES);

  return (
    <SolarMenu.Item
      name={ResourceName.HELP_ARTICLES}
      icon={<HelpIcon />}
      label="Help / KB"
      subMenu={
        <>
          <SubMenuHeading>View</SubMenuHeading>
          <Divider />
          <SolarMenu.List dense>
            <SolarMenu.Item name="help.center" to="/help" label="Help Center" />
            <SolarMenu.Item name="help.articles" to="/help-articles?filter={}" label="Manage Articles" />
          </SolarMenu.List>
          {canCreate && (
            <ActionsSection>
              <SolarMenu.Item name="help.create" to="/help-articles/create" label="New Article" icon={<Add />} />
            </ActionsSection>
          )}
        </>
      }
    />
  );
};
